// ============================================
// FAMUZA MARKET — Navbar
// Jumia-style: Logo + Cart + Sell + Hamburger
// Search bar is on homepage, not in navbar
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 769);
  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export default function Navbar() {
  const { cartCount, user } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  return (
    <>
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200,
        background: '#0D0D0D',
        borderBottom: '0.5px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(12px)',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          padding: '0 1rem',
          height: 52,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          boxSizing: 'border-box',
          width: '100%',
        }}>

          {/* Hamburger — mobile left */}
          {isMobile && (
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'none', border: 'none', color: '#F5F0E8', fontSize: '1.2rem', cursor: 'pointer', padding: '0.25rem', flexShrink: 0 }}>
              {menuOpen ? '✕' : '☰'}
            </button>
          )}

          {/* Logo */}
          <Link to="/" style={{ flexShrink: 0 }}>
            <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '1.1rem' : '1.3rem', letterSpacing: '-0.02em', color: '#F5F0E8', whiteSpace: 'nowrap' }}>
              FAMUZA <span style={{ color: '#E8622A' }}>Market</span>
            </span>
          </Link>

          {/* Desktop search — middle */}
          {!isMobile && (
            <div style={{ flex: 1, maxWidth: 500 }}>
              <Link to="/listings">
                <div style={{ background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 100, padding: '0.5rem 1rem', fontSize: '0.85rem', color: 'rgba(245,240,232,0.4)', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  🔍 Search listings...
                </div>
              </Link>
            </div>
          )}

          {/* Spacer — push right items to end on mobile */}
          <div style={{ flex: 1 }} />

          {/* Desktop nav links */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.85rem' }}>
              <Link to="/listings" style={{ color: 'rgba(245,240,232,0.6)' }}>Browse</Link>
              <Link to="/listings?deals=true" style={{ color: 'rgba(245,240,232,0.6)' }}>Deals</Link>
            </div>
          )}

          {/* Cart */}
          <Link to="/cart" style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1E1E1E', border: '0.5px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
              🛒
            </div>
            {cartCount > 0 && (
              <span style={{ position: 'absolute', top: -3, right: -3, background: '#E8622A', color: '#fff', fontSize: '0.55rem', fontWeight: 700, width: 15, height: 15, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            )}
          </Link>

          {/* Account icon — always visible on mobile */}
          <Link to={user ? (user.role === 'admin' ? '/admin' : '/dashboard') : '/login'} style={{ flexShrink: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#1E1E1E', border: `0.5px solid ${user ? 'rgba(232,98,42,0.4)' : 'rgba(255,255,255,0.1)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
              {user ? (
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.82rem', color: '#E8622A' }}>{user.name?.[0]?.toUpperCase() || 'U'}</span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(245,240,232,0.6)" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              )}
            </div>
          </Link>

          {/* Sell button */}
          {(!isMobile || !user) && (
            <Link to="/sell" style={{ flexShrink: 0 }}>
              <button style={{ background: '#E8622A', color: '#fff', border: 'none', borderRadius: 100, padding: '0.4rem 0.875rem', fontSize: '0.78rem', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {isMobile ? 'Sell' : 'Start selling'}
              </button>
            </Link>
          )}

          {/* Desktop Log in */}
          {!isMobile && !user && (
            <Link to="/login" style={{ flexShrink: 0 }}>
              <button style={{ background: 'transparent', color: 'rgba(245,240,232,0.7)', border: '0.5px solid rgba(255,255,255,0.15)', borderRadius: 100, padding: '0.4rem 1rem', fontSize: '0.82rem', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>Log in</button>
            </Link>
          )}
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      {menuOpen && (
        <div style={{ position: 'fixed', top: 52, left: 0, right: 0, zIndex: 199, background: '#161616', borderBottom: '0.5px solid rgba(255,255,255,0.07)', padding: '1rem' }}>
          {[
            ['🏠', 'Home', '/'],
            ['🗂️', 'Browse listings', '/listings'],
            ['🔥', 'Deals', '/listings?deals=true'],
            ['🏪', 'Start selling', '/sell'],
            ['👟', 'Shoes', '/listings?cat=Shoes'],
            ['👗', 'Salaula', '/listings?cat=Salaula'],
            ['🏡', 'Household', '/listings?cat=Household'],
            ['📱', 'Electronics', '/listings?cat=Electronics'],
            ['🔐', 'Log in', '/login'],
          ].map(([icon, label, path]) => (
            <Link key={path+label} to={path} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0.5rem', borderBottom: '0.5px solid rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.75)', fontSize: '0.9rem', textDecoration: 'none' }}>
              <span>{icon}</span> {label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
